// The app: sign-in, navigation, loading and saving
import { CUR, baseRates, effFx, expenseMap, fxAs, grandTotal, projectCcy, setExpenseMap, setSharedFx } from '../core/budget.js';
import { h, useEffect, useMemo, useRef, useState } from '../core/react.js';
import { MONO, T } from '../core/theme.js';
import { initFX } from '../core/fx.js';
import { ProjectContext, makeProject } from '../core/project.js';
import { Login } from './login.js';
import { Sidebar } from './sidebar.js';
import { TopBar, VIEW_NAMES } from './topbar.js';
import { Dashboard } from '../pages/dashboard.js';
import { BudgetView } from '../pages/budget.js';
import { Invoices } from '../pages/invoices.js';
import { Quotes } from '../pages/quotes.js';
import { PDFPreview } from '../pages/estimate.js';
import { ApprovalsPage, ApprovalBar } from '../pages/approvals.js';
import { Settings } from '../pages/settings.js';
import { CrewPage, copyCrewToSchedules, crewSyncKey } from '../pages/crew.js';
import { FilesPage } from '../pages/files.js';
import { DeliveriesPage } from '../pages/deliveries.js';
import { DeadlinesPage } from '../pages/deadlines.js';
import { CopyProjectPage } from '../pages/copy-project.js';
import { SubBudgetsPage } from '../pages/subbudgets.js';
import { SuppliersPage, importBuiltinSuppliers } from '../pages/suppliers.js';
import { SystemPage } from '../pages/system.js';
import { ActivityPage } from '../pages/activity.js';
import { ProjectPage } from '../pages/project.js';
import { TeamManager } from '../pages/team.js';
import { wipeLocalData } from '../core/util.js';
import { API } from '../core/api.js';
import { J, lineClashes, merge3, normalizeProject, rid, stripLocal } from '../core/sync.js';
import { MODULES, ModuleAreas } from './modules.js';
import { shell, moduleHost } from '../../modules/host.js';
import { useNarrow } from '../ui/narrow.js';
import { SyncDot } from '../ui/widgets.js';

function BootScreen({ text }) {
  return h("div", { style: { height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.navy, color: 'rgba(255,255,255,.7)', fontSize: 13 } }, text);
}

export function App() {
  const [user, setUser] = useState(undefined);
  const lastUser = useRef(null);   // who was signed in before the session ended
  if (user) lastUser.current = user;
  const [view, setViewRaw] = useState('dashboard');
  // phones: the menu is a panel that slides in from the left, behind a button
  const narrow = useNarrow();
  const [menuOpen, setMenuOpen] = useState(false);
  const [opened, setOpened] = useState([]);
  const viewRef = useRef('dashboard');
  const setView = v => {
    viewRef.current = v;
    if (MODULES[v]) setOpened(o => o.includes(v) ? o : [...o, v]);
    setViewRaw(v);
  };
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeId, setActiveIdRaw] = useState(() => { try { return localStorage.getItem('glassbox-prism:active') || null; } catch (_) { return null; } });
  const setActiveId = id => { try { localStorage.setItem('glassbox-prism:active', id || ''); } catch (_) {} setActiveIdRaw(id); };
  const [fx, setFx] = useState(initFX);
  const [syncState, setSyncState] = useState({ ok: true, at: null, msg: '' });
  const base = useRef({});          // id -> {version, json, data}: the last copy known to be on the server
  const projectsRef = useRef(projects);
  projectsRef.current = projects;
  const saving = useRef(new Map());   // id -> promise
  const timers = useRef({});
  const fxBase = useRef(null);
  const readOnly = user?.role === 'Read-only' || user?.role === 'Freelancer';   // freelancers never save budgets
  // freelancers start on the schedule and only reach production pages
  useEffect(() => { if (user?.role === 'Freelancer' && !['schedule', 'crew', 'deliveries', 'files', 'team'].includes(view)) setViewRaw('schedule'); }, [user?.role, view]);
  // Schedule / Tenders fetch the latest when they come on screen
  useEffect(() => { if (MODULES[view]) setTimeout(() => moduleHost(view).kick(), 50); }, [view]);

  // ── session
  useEffect(() => {
    API.call('me').then(r => setUser(r.stage && r.stage !== 'full' ? null : r.user)).catch(() => setUser(null));
    const out = () => setUser(null);
    window.addEventListener('prism-signed-out', out);
    return () => window.removeEventListener('prism-signed-out', out);
  }, []);

  // ── load and poll
  function acceptRemote(id, version, data, by) {
    const b = base.current[id];
    const remote = normalizeProject({ ...makeProject(), ...data, id });
    const local = projectsRef.current.find(p => p.id === id);
    const dirty = local && b && J(stripLocal(local)) !== b.json;
    base.current[id] = { version, json: J(stripLocal(remote)), data: stripLocal(remote) };
    let next = remote;
    if (dirty && b) {
      next = normalizeProject({ ...merge3(b.data, stripLocal(local), stripLocal(remote)), id });
      const clash = lineClashes(b.data, stripLocal(local), stripLocal(remote));
      if (clash.length) setTimeout(() => flashSync(`${by || 'Someone'} changed ${clash.map(c => `“${c.desc}” (their ${c.theirs.join(', ')})`).join('; ')} while you were editing it. Your version was kept; check ${clash.length > 1 ? 'those lines' : 'that line'}.`, 'warn'), 0);
    }
    setProjects(prev => prev.some(p => p.id === id) ? prev.map(p => p.id === id ? next : p) : [...prev, next].sort((a, c) => (a.createdAt || 0) - (c.createdAt || 0)));
  }
  async function pull() {
    const known = Object.fromEntries(Object.entries(base.current).map(([id, b]) => [id, b.version]));
    const r = await API.call('projects.list', { known });
    Object.entries(r.projects || {}).forEach(([id, p]) => acceptRemote(id, p.version, p.data, p.by));
    const gone = Object.keys(base.current).filter(id => !(id in (r.ids || {})));
    if (gone.length) {
      gone.forEach(id => delete base.current[id]);
      setProjects(prev => prev.filter(p => !gone.includes(p.id)));
    }
    setSyncState(s => ({ ...s, ok: true, at: Date.now(), msg: '' }));
  }
  useEffect(() => {
    if (!user) return;
    let stop = false;
    (async () => {
      try {
        await pull();
        const s = await API.call('settings.get', { key: 'fx' });
        if (s.data) { fxBase.current = J(s.data); setFx(s.data); } else fxBase.current = J(initFX);
      } catch (e) { setSyncState({ ok: false, at: null, msg: e.message }); }
      if (!stop) setLoaded(true);
    })();
    const pullFailed = e => API.report(e, 'sync: refresh from server');
    const t = setInterval(() => { if (!document.hidden) pull().catch(pullFailed); }, 45000);
    const onVis = () => { if (!document.hidden) pull().catch(pullFailed); };
    document.addEventListener('visibilitychange', onVis);
    const onFocus = () => pull().catch(pullFailed);
    window.addEventListener('focus', onFocus);
    return () => { stop = true; clearInterval(t); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onVis); };
  }, [user?.id]);

  // ── save changed projects (debounced), merging if someone else saved first
  async function saveProject(id) {
    if (saving.current.has(id)) { await saving.current.get(id); }
    const local = projectsRef.current.find(p => p.id === id);
    if (!local || readOnly) return;
    const data = stripLocal(local), json = J(data), b = base.current[id];
    if (b && b.json === json) return;
    const run = (async () => {
      try {
        const r = await API.call('project.save', { id, data, baseVersion: b ? b.version : null });
        if (r.conflict) { acceptRemote(id, r.version, r.data, r.by); return; }
        base.current[id] = { version: r.version, json, data };
        setSyncState({ ok: true, at: Date.now(), msg: '' });
      } catch (e) {
        if (e.status === 410) { delete base.current[id]; setProjects(prev => prev.filter(p => p.id !== id)); return; }
        setSyncState({ ok: false, at: null, msg: e.message });
        clearTimeout(timers.current[id]); timers.current[id] = setTimeout(() => saveProject(id), 5000);
      }
    })();
    saving.current.set(id, run);
    try { await run; } finally { saving.current.delete(id); }
  }
  useEffect(() => {
    if (!loaded || readOnly) return;
    projects.forEach(p => {
      const b = base.current[p.id];
      if (b && b.json === J(stripLocal(p))) return;
      clearTimeout(timers.current[p.id]);
      timers.current[p.id] = setTimeout(() => saveProject(p.id), 800);
    });
  }, [projects, loaded]);
  // Sign out: send unsaved edits first, warn about offline schedule changes, then clear this device
  async function signOut() {
    const pending = (() => { try { return JSON.parse(localStorage.getItem('gbx-pending-v2') || '{}'); } catch (_) { return {}; } })();
    if (Object.keys(pending).length && !window.confirm('Some schedule changes made offline have not reached the server yet. Signing out deletes them from this device. Sign out anyway?')) return;
    await flushSaves().catch(() => {}); await API.call('logout').catch(() => {}); wipeLocalData(); location.reload();
  }
  const signOutRef = useRef(signOut); signOutRef.current = signOut;
  useEffect(() => {
    const go = () => signOutRef.current();
    window.addEventListener('prism-sign-out', go);
    return () => window.removeEventListener('prism-sign-out', go);
  }, []);
  async function flushSaves() {
    for (const id of Object.keys(timers.current)) { clearTimeout(timers.current[id]); delete timers.current[id]; }
    await Promise.all(projectsRef.current.map(p => saveProject(p.id)));
  }
  useEffect(() => {
    if (!loaded || readOnly || fxBase.current == null || J(fx) === fxBase.current) return;
    const t = setTimeout(() => API.call('settings.save', { key: 'fx', data: fx }).then(() => { fxBase.current = J(fx); }).catch(e => flashSync(e.message, 'err')), 900);
    return () => clearTimeout(t);
  }, [fx, loaded]);
  useEffect(() => {
    const warn = e => { if (projectsRef.current.some(p => { const b = base.current[p.id]; return !b || b.json !== J(stripLocal(p)); })) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  const project = projects.find(p => p.id === activeId) || projects[0] || null;
  const sections = project?.sections || [];
  // the open project's currencies and rates drive every figure on screen
  setSharedFx(fx);
  const [viewAsByProject, setViewAsByProject] = useState({});
  const viewAsWanted = view === 'budget' && project ? viewAsByProject[project.id] || null : null;
  const fxView = useMemo(() => viewAsWanted ? fxAs(project, viewAsWanted) : null, [viewAsWanted, project?.fxLocked, project?.fx, fx]);
  const viewAs = fxView ? viewAsWanted : null;
  const own = projectCcy(project), ownRates = baseRates(project);
  Object.assign(CUR, viewAs ? { cost: viewAs, bill: viewAs, own, viewAs, abK: Number(ownRates[own.bill]?.usd) / Number(ownRates[viewAs]?.usd) || 1 } : { ...own, own, viewAs: null, abK: 1 });
  setExpenseMap(useMemo(() => expenseMap(projects), [projects]));
  // Copy the crew into linked schedules whenever either changes, whichever page is open
  const crewSynced = useRef({});
  const crewKey = crewSyncKey(project);
  useEffect(() => {
    if (!project || !loaded || !user || ['Read-only', 'Freelancer'].includes(user.role) || !(project.links?.schedule || []).length) return;
    if (crewSynced.current[project.id] === crewKey) return;
    const p = project;
    const t = setTimeout(() => { crewSynced.current[p.id] = crewKey; copyCrewToSchedules(p).then(notes => { if (notes.length) API.report(new Error(notes.join(' ')), 'crew: copy to schedule'); }).catch(e => API.report(e, 'crew: copy to schedule')); }, 1500);
    return () => clearTimeout(t);
  }, [crewKey, loaded, user?.role]);
  const fxEff = useMemo(() => effFx(project), [project?.costCcy, project?.billCcy, project?.fxLocked, project?.fx, fx]);
  const missingRates = useMemo(() => grandTotal(sections, fxEff).missing, [sections, fxEff]);
  const anyDirty = projects.some(p => { const b = base.current[p.id]; return !b || b.json !== J(stripLocal(p)); });
  const synced = syncState.ok && !anyDirty;
  const lastFetch = syncState.at;

  function updateProject(patch) {
    setProjects(prev => {
      const target = prev.find(p => p.id === (project && project.id)) || prev[0];
      if (!target) return prev;
      return prev.map(p => p.id === target.id ? normalizeProject({ ...p, ...(typeof patch === 'function' ? patch(p) : patch) }) : p);
    });
  }
  function setSections(newSections) {
    updateProject(p => ({ sections: typeof newSections === 'function' ? newSections(p.sections || []) : newSections }));
  }
  function startNewProject() {
    if (readOnly) return;
    const blank = normalizeProject(makeProject({ id: rid('p_'), name: 'New project', client: '', date: '', status: 'Planning', createdAt: Date.now() }));
    setProjects(prev => [...prev, blank]);
    setActiveId(blank.id);
    setView('project');
  }
  // New project from a past event (pages/copy-project.js has already saved it and its schedule copies)
  function createFromPast({ id, version, data }) {
    acceptRemote(id, version, data);
    setActiveId(id);
    setView('project');
  }
  function switchProject(p) {
    setActiveId(p.id);
    setView('budget');
  }
  async function deleteProject(id) {
    clearTimeout(timers.current[id]);
    try {
      if (base.current[id]) await API.call('project.delete', { id });
      delete base.current[id];
      setProjects(prev => prev.filter(p => p.id !== id));
      setView('dashboard');
    } catch (e) { flashSync(e.message, 'err'); }
  }
  const ctxValue = useMemo(() => ({ project, sections, fx: fxEff, projects, user: user || lastUser.current }), [project, sections, fxEff, projects, user, activeId]);
  useEffect(() => {
    if (!MODULES[view] || !project) return;
    const id = (project.links?.[view] || [])[0];
    if (!id) return;
    let tries = 0, stop = false;
    const go = () => {
      if (stop) return;
      if (!moduleHost(view).open(id) && ++tries < 30) setTimeout(go, 400);
    };
    go();
    return () => { stop = true; };
  }, [view, project?.id]);
  // Expenses logged before Costs existed become receipts in Costs (once, by the first editor who opens the project).
  const migrating = useRef(new Set());
  useEffect(() => {
    const p = project;
    if (!loaded || !user || readOnly || !p || !(p.expenses || []).length || migrating.current.has(p.id)) return;
    migrating.current.add(p.id);
    (async () => {
      const items = (p.sections || []).flatMap(s => s.items || []);
      const left = [];
      let moved = 0;
      for (const e of p.expenses) {
        const it = items.find(i => i.id === e.itemId) || items.find(i => !e.itemId && i.desc === e.line);
        const amt = Number(e.amt) || 0;
        const r = it && fxEff[e.ccy] && fxEff[it.ccy] ? fxEff[e.ccy].gbp / fxEff[it.ccy].gbp : NaN;
        if (!it || !(amt > 0) || !isFinite(r)) { left.push(e); continue; }
        const net = amt / (1 + (Number(it.vat) || 0));
        const res = await costAction('saveInvoice', {
          invoice: { supplier: e.note || 'Expense', number: '', date: e.date || '', due: '', ccy: e.ccy, net: amt, vat: 0, gross: amt,
            description: e.note || '', notes: 'Moved from the old Expenses log', kind: 'receipt', paidBy: e.by || '', method: '' },
          allocations: [{ row: it._sheetRow, desc: it.desc, ln: it.ln, lineCcy: it.ccy, gross: amt, net, fx: r, netLine: net * r }],
          markInvoiced: false, force: true
        });
        if (res.ok) moved++; else left.push(e);
      }
      updateProject(q => q.id === p.id ? { expenses: left } : {});
      if (moved) flashSync(`${moved} logged expense${moved > 1 ? 's were' : ' was'} moved into Costs as receipts`);
    })().catch(err => { migrating.current.delete(p.id); console.warn(err); });
  }, [loaded, project?.id, (project?.expenses || []).length, readOnly]);
  const [syncToast, setSyncToast] = useState(null);
  function flashSync(msg, type = 'ok') {
    setSyncToast({ msg, type });
    clearTimeout(flashSync.t);
    flashSync.t = setTimeout(() => setSyncToast(null), type === 'warn' ? 12000 : type === 'err' ? 5000 : 2500);
  }

  // ── what Schedule and Tenders need from the main app (see src/modules/host.js)
  const gbx = useRef({});
  gbx.current = { user, setView, fx, projects, project, setActiveId };
  // filled in while drawing (not in an effect): a module starting in a child's effect would run before ours
  if (!shell.user) Object.assign(shell, {
    user: () => { const u = gbx.current.user || {}; return { id: u.id, name: u.name, role: u.role }; },
    isActive: mod => viewRef.current === mod,
    call: async (action, body) => {
      if (String(body?.coll || '').startsWith('tenders/directory')) await importBuiltinSuppliers(gbx.current.user?.role).catch(e => API.report(e, 'suppliers: import built-in list'));
      if (action === 'ai') { const r = await API.aiJob({ kind: 'prompt', prompt: body.prompt, json: body.json, maxTokens: body.maxTokens }); return { text: r.text, truncated: r.truncated }; }
      return API.call(action, body);
    },
    ai: (params, opts) => API.aiJob({ kind: 'prompt', prompt: params.prompt, json: params.json, maxTokens: params.maxTokens }, opts),
    fx: () => gbx.current.fx,
    toBudget: payload => {
      const { projects: ps, project: cur } = gbx.current;
      const linked = ps.find(p => (p.links?.tenders || []).includes(payload.eventId));
      if (linked) gbx.current.setActiveId(linked.id);
      else if (cur) {
        const q = payload.quote || {};
        q.notes = `This tender isn't linked to a budget, so it's going into "${cur.name}". To send it to another budget, link the tender on that project's Project settings page.` + (q.notes ? ' ' + q.notes : '');
      }
      window.__prismHandoff = payload;
      gbx.current.setView('quotes');
    },
    openSuppliers: filter => { window.__prismSuppliersFilter = filter; gbx.current.setView('suppliers'); }
  });

  // Signing in again as the same person (session expired) keeps unsaved edits and open screens;
  // they are saved as soon as the new session exists. A different person starts clean.
  const onLogin = u => {
    try { const prev = localStorage.getItem('prism-last-user'); if (prev && prev !== u.id) wipeLocalData(); localStorage.setItem('prism-last-user', u.id); } catch (_) {}
    if (lastUser.current && lastUser.current.id === u.id) { setUser(u); setTimeout(() => { flushSaves().catch(e => API.report(e, 'sync: save after signing in again')); pull().catch(e => API.report(e, 'sync: refresh after signing in again')); }, 0); return; }
    // Schedule and Tenders keep the previous person's data in memory once started, so start the page afresh
    if (opened.length) { window.location.reload(); return; }
    lastUser.current = null; setLoaded(false); base.current = {}; setProjects([]); setOpened([]); setUser(u);
  };
  if (user === undefined) return h(BootScreen, { text: 'Opening Prism…' });
  if (!user && !lastUser.current) return h(Login, { onLogin });
  if (!loaded) return h(BootScreen, { text: 'Loading projects…' });
  const me = user || lastUser.current;

  function applyServerProject(p) { if (p) acceptRemote(p.id, p.version, p.data); }
  // Costs and Quote to budget: reading documents with Claude, and saving invoices and quotes against budget lines
  async function costAction(action, payload = {}) {
    const p = project;
    if (!p) return { ok: false, error: 'Create or pick a project first.' };
    try {
      if (action === 'listInvoices') return { ok: true, ...(await API.call('invoices.list', { projectId: p.id })) };
      if (action === 'listQuotes') return { ok: true, ...(await API.call('quotes.list', { projectId: p.id })) };
      if (action === 'extractInvoice' || action === 'extractQuote') {
        const fileKey = await API.upload(p.id, 'upload', payload.mediaType, payload.fileData);
        const r = await API.aiJob({ kind: action, fileKey });
        return { ok: true, [action === 'extractInvoice' ? 'invoice' : 'quote']: r.result || {} };
      }
      await flushSaves();
      if (!base.current[p.id]) return { ok: false, error: 'This project is still being saved. Try again in a moment.' };
      const fileKey = payload.fileData ? await API.upload(p.id, 'upload', payload.mediaType, payload.fileData) : '';
      let r;
      if (action === 'saveInvoice') r = await API.call('invoice.save', { projectId: p.id, invoice: payload.invoice, allocations: payload.allocations, markInvoiced: payload.markInvoiced, force: payload.force, fileKey });
      else if (action === 'setInvoiceStatus') r = await API.call('invoice.status', { id: payload.id, status: payload.status });
      else if (action === 'updateInvoice') r = await API.call('invoice.update', { id: payload.id, invoice: payload.invoice });
      else if (action === 'deleteInvoice') r = await API.call('invoice.delete', { id: payload.id });
      else if (action === 'applyQuote') r = await API.call('quote.apply', { projectId: p.id, quote: payload.quote, subBudget: payload.subBudget, lines: payload.lines, fileKey });
      else return { ok: false, error: 'Unknown action ' + action };
      if (r.project) applyServerProject(r.project);
      return r;
    } catch (e) {
      return { ok: false, error: e.message, ...(e.data || {}) };
    }
  }
  async function reloadBudget() { await pull().catch(e => API.report(e, 'budget: reload')); }

  const VIEWS = {
    dashboard: h(Dashboard, { projects, project, sections, fx: fxEff, setView, switchProject, onAddProject: startNewProject }),
    budget: h(React.Fragment, null, project && h(ApprovalBar, { project, sections, fx: fxEff, setView }), h(BudgetView, { project, sections, setSections, fx: fxView || fxEff, setView, flashSync, user: me, viewAs,
      viewChoices: Object.keys(fxEff), setViewAs: c => setViewAsByProject(m => ({ ...m, [project.id]: c || null })) })),
    quotes: h(Quotes, { project, sections, fx: fxEff, user: me, costAction, reloadBudget, flashSync, setView }),
    invoices: h(Invoices, { project, sections, fx: fxEff, user: me, costAction, reloadBudget, flashSync, setView }),
    pdf: h(PDFPreview, { project, sections, fx: fxEff, user: me, updateProject }),
    approvals: h(ApprovalsPage, { project, sections, fx: fxEff, user: me, updateProject, setView, flashSync }),
    team: h(TeamManager, { currentUser: me, onSelfChange: u => setUser(u) }),
    crew: h(CrewPage, { project, user: me?.role === 'Freelancer' ? { ...me, role: 'Read-only' } : me, updateProject, flashSync }),
    files: h(FilesPage, { project, user: me, setView, flashSync }),
    deliveries: h(DeliveriesPage, { project, user: me, flashSync }),
    deadlines: h(DeadlinesPage, { project, user: me, updateProject, flashSync }),
    subbudgets: h(SubBudgetsPage, { project, sections, fx: fxEff, user: me, updateProject, flashSync }),
    suppliers: h(SuppliersPage, { user: me, flashSync }),
    system: h(SystemPage, { user: me, flashSync }),
    activity: h(ActivityPage, { user: me, project, projects, onChanged: applyServerProject, reload: () => pull(), flashSync }),
    project: h(ProjectPage, { project, user: me, updateProject, setSections, onDelete: deleteProject, onAddProject: startNewProject, flashSync,
      onImported: async pid => { await flushSaves(); const r = await API.call('project.recompute', { projectId: pid }); applyServerProject(r.project); return r.orphaned; } }),
    settings: h(Settings, { project, fx, setFx, user: me }),
    copy: h(CopyProjectPage, { projects, user: me, onCreated: createFromPast, flashSync })
  };
  return h(ProjectContext.Provider, { value: ctxValue },
    h("div", { style: { display: 'flex', height: narrow ? '100dvh' : '100vh', overflow: 'hidden', position: 'relative' } },
      syncToast && h("div", {
        role: syncToast.type === 'err' ? 'alert' : 'status',   // read out by screen readers
        style: { position: 'fixed', bottom: 20, right: 20, zIndex: 600, padding: '8px 16px', maxWidth: 420,
          background: syncToast.type === 'err' ? T.redBg : syncToast.type === 'warn' ? T.amberBg : T.greenBg, border: `1px solid ${syncToast.type === 'err' ? T.redBr : syncToast.type === 'warn' ? T.amberBr : T.greenBr}`,
          color: syncToast.type === 'err' ? T.red : syncToast.type === 'warn' ? T.amber : T.green, fontSize: 11, fontWeight: 500, ...MONO, boxShadow: '0 4px 16px rgba(0,0,0,.15)', animation: 'fadeUp .2s ease both' }
      }, syncToast.type === 'err' ? '✕ ' : syncToast.type === 'warn' ? '! ' : '✓ ', syncToast.msg),
      narrow
        ? h(React.Fragment, null,
            menuOpen && h("div", { onClick: () => setMenuOpen(false), style: { position: 'fixed', inset: 0, background: 'rgba(20,21,24,.45)', zIndex: 900 } }),
            h("div", { style: { position: 'fixed', top: 0, left: 0, height: '100dvh', zIndex: 901, transform: menuOpen ? 'none' : 'translateX(-100%)', transition: 'transform .2s ease', boxShadow: menuOpen ? '0 0 24px rgba(0,0,0,.3)' : 'none' } },
              h(Sidebar, { view, setView: v => { setView(v); setMenuOpen(false); }, project, projects, setProject: switchProject, synced, lastFetch, user: me,
                onAddProject: () => { setMenuOpen(false); startNewProject(); }, onSignOut: signOut, height: '100dvh' })))
        : h(Sidebar, { view, setView, project, projects, setProject: switchProject, synced, lastFetch, user: me, onAddProject: startNewProject,
            onSignOut: signOut }),
      h("div", { style: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: T.ivory } },
        narrow && h("div", { style: { height: 48, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '0 10px 0 4px', background: T.navy, color: T.white } },
          h("button", { onClick: () => setMenuOpen(true), 'aria-label': 'Open the menu', style: { width: 44, height: 44, color: T.white, fontSize: 22, lineHeight: 1 } }, '\u2630'),
          h("div", { style: { minWidth: 0, flex: 1 } },
            h("div", { style: { fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, VIEW_NAMES[view] || view),
            project && h("div", { style: { fontSize: 11, color: 'rgba(255,255,255,.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, project.name)),
          // whether edits have saved, which on a computer is at the foot of the sidebar
          h("div", { style: { flexShrink: 0 } }, h(SyncDot, { synced, lastFetch }))),
        !syncState.ok && h("div", { style: { padding: '7px 20px', fontSize: 12, color: T.red, background: T.redBg, borderBottom: `1px solid ${T.redBr}` } },
          `Changes aren't reaching the server: ${syncState.msg}. They stay on this screen and will be retried; keep this tab open.`),
        !MODULES[view] && missingRates.length > 0 && ['dashboard', 'budget', 'invoices', 'quotes', 'pdf', 'approvals'].includes(view) && h("div", {
          style: { padding: '7px 20px', fontSize: 12, color: T.amber, background: T.amberBg, borderBottom: `1px solid ${T.amberBr}` } },
          `Lines in ${missingRates.join(', ')} have no exchange rate and count as zero. `,
          h("a", { href: '#', onClick: e => { e.preventDefault(); setView(project?.fxLocked ? 'project' : 'settings'); }, style: { color: T.amber, fontWeight: 600 } }, project?.fxLocked ? 'Add it to this project\'s rates' : 'Add it in Settings')),
        !MODULES[view] && !narrow && h(TopBar, { project, sections, view }),
        !MODULES[view] && h("main", { style: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }, key: view + project?.id, className: "fade-in" },
          !project && !['dashboard', 'team', 'settings', 'activity', 'suppliers', 'system', 'copy'].includes(view)
            ? h("div", { style: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 } },
                h("div", { style: { fontSize: 15, fontWeight: 600, color: T.coal } }, "No projects yet"),
                !readOnly && h("button", { onClick: startNewProject, style: { padding: '9px 18px', background: T.navy, color: T.white, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer' } }, "Create a project"))
            : (VIEWS[view] || VIEWS.dashboard)),
        h(ModuleAreas, { view, opened }))),
      !user && h("div", { style: { position: 'fixed', inset: 0, zIndex: 1000 } },
        h("div", { style: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2, padding: '9px 20px', fontSize: 12.5, background: T.amberBg, color: T.amber, borderBottom: `1px solid ${T.amberBr}` } },
          'Your session ended. Sign in again as ' + lastUser.current.email + ' to save your changes; nothing you did has been lost.'),
        h(Login, { onLogin, initialEmail: lastUser.current.email })));
}
